import { create, valueOf, Any } from 'microstates';
import HistoryState from './history-state';
import TextState from './text-state';
import ErrorsState from './errors-state';
import HeadersState from './headers-state';
import OccasionSelectListState from './occasion-select-list-state';
import ExternalActionsState from './external-actions-state';
import ColorState from './color';
import FontState from './font';
import { validationTypes } from './notification-state';
import { LINE_LENGTH } from '../lib/constants';
import { Y_EM } from '@lovepop/svg-writer';
import { stripSpecialCharacters } from '../lib/text-utils';
import { arraysEqual } from '../lib/array-utils';

const { SPECIAL_CHAR, LENGTH } = validationTypes;

export default class AppState {
  loaded = Boolean;
  isHoveringText = Boolean;
  isEditing = Boolean;
  isSaving = Boolean;
  layout = String;
  errors = ErrorsState;
  textContainer = Any;

  didAgreeToTerms = Boolean;
  noticeError = Any;
  showColorModal = Boolean;
  showFontModal = Boolean;
  terms = String;

  externalActions = ExternalActionsState;

  // PremadeMessages
  isChoosingMessage = Boolean;
  occasionSelectList = OccasionSelectListState;

  // These are sort of the same
  svg = String;
  shouldRenderFromServer = Boolean;

  // API
  noteResourceId = Number;
  headers = HeadersState;
  endpoint = String;
  printSvg = Any;

  // TypesetState
  text = TextState;
  spaceChar = String;
  center = Boolean;
  withHyphenation = Boolean;
  lineLengths = Array;
  tolerance = Number;
  format = String;
  textColors = [ColorState];
  textColor = ColorState;
  fontFaces = [FontState];
  fontFace = FontState;

  initialize({
    isEditing,
    isHoveringText,
    headers,
    noteResourceId,
    shouldRenderFromServer,
    spaceChar = 'n',
    withHyphenation = false,
    center = false,
    lineLengths = [LINE_LENGTH],
    tolerance = 5,
    format = 'left'
  }) {

    let initialized = this;
    initialized = isEditing ? initialized.isEditing.set(isEditing) : initialized;
    initialized = isHoveringText ? initialized.isHoveringText.set(isHoveringText) : initialized;
    initialized = headers ? initialized.headers.set(headers) : initialized;
    initialized = noteResourceId ? initialized.noteResourceId.set(noteResourceId) : initialized;
    initialized = initialized.shouldRenderFromServer.set(shouldRenderFromServer);
    initialized = initialized.spaceChar.set(spaceChar);
    initialized = initialized.center.set(center);
    initialized = initialized.withHyphenation.set(withHyphenation);
    initialized = initialized.lineLengths.set(lineLengths);
    initialized = initialized.tolerance.set(tolerance);
    initialized = initialized.format.set(format);

    return initialized;
  }

  get profanityMessage() {
    return `Sorry, certain words in your message violate our <a href="${this.terms.state}" target="_blank" rel="noopener noreferrer">Content Policy</a>. They have been changed to asterisks.`
  }

  get lineHeight() {
    // This might be off when we change fonts
    return Y_EM * this.fontFace.size.state;
  }

  get lineLimit() {
    return Math.floor(this.maxHeight / this.lineHeight);
  }

  get numberOfLines() {
    return Math.floor(Math.ceil(this.textContainerHeight) / this.lineHeight);
  }

  get overLineLimit() {
    return this.numberOfLines > this.lineLimit;
  }

  validatePrintability() {
    let state = this;
    if (state.textContainerHeight > 0) {
      /* remove errors if no longer over the limit */
      if (!state.overLineLimit) {
        return state.errors.errors.filter(x => x.validationType.state !== LENGTH);
      } else {
        /* add an error if there's no error */
        if (state.hasLengthErrors) {
          return state;
        } else {
          state = state.errors.errors.filter(x => x.validationType.state !== LENGTH);
          return  state.addError('Your message exceeds the printing area for the note', { validationType: LENGTH })
        }
      }
    }

    return state;
  }

  log(err) {
    this.noticeError.state(err);
    return this;
  }

  toggleFormat() {
    const format = this.format.state === 'left' ? 'center' : 'left';
    return this.format.set(format).typeset();
  }

  typeset() {
    return this.setText(this.getText);
  }

  setColor(color) {
    return this.textColor.set(color).typeset().showColorModal.toggle();
  }

  setFontFace(fontFace) {
    return this.fontFace.set(fontFace).typeset().showFontModal.toggle();
  }

  get hasProfanityWarning() {
    return valueOf(this.errors.errors)
      .map(x => x.message)
      .includes(this.profanityMessage);
  }

  clearProfanityWarnings() {
    return this.errors.errors.filter(x => x.message.state !== this.profanityMessage);
  }


  handleSpecialCharacters(text) {
    let state = this;
    const { errorMessage, specialCharacters } = stripSpecialCharacters(text);

    let newState = state.text.setText(text)
      .text.specialCharactersArray.set(specialCharacters);

    const existingSpecialChars = state.text.specialCharacters;
    const arraysNotEqual = !arraysEqual(specialCharacters, existingSpecialChars)

    if (newState.hasSpecialCharErrors && arraysNotEqual) {
      newState = newState.errors.errors.filter(x =>  x.validationType.state !== SPECIAL_CHAR);
      return errorMessage
        ? newState.addWarning(errorMessage, { validationType: SPECIAL_CHAR })
        : newState;
    }

    if (errorMessage && !newState.hasSpecialCharErrors) {
      newState = newState.addWarning(errorMessage, { validationType: SPECIAL_CHAR });
      return newState;
    }

    return newState;
  }

  setText(text) {
    let newState = this.handleSpecialCharacters(text);

    /*
     * NOTE: We're validating number of lines
     * in the `afterUpdate` hook in NotePreview.html
     * We can't do it in app-state since we need to
     * wait for the text to render before we can
     * measure it.
     */

    return newState.svg.set(null);
  }

  addError(error, meta = {}) {
    return this.errors.add(error, meta);
  }

  addWarning(error, meta = {}) {
    return this.errors.addWarning(error, meta);
  }

  addProfanityWarning() {
    return this.errors.addWarning(this.profanityMessage);
  }

  setOccasions(occasions) {
    return this.occasionSelectList.occasions.set(occasions);
  }

  selectOccasion(title) {
    return this.occasionSelectList.selectFrom(title)
  }

  deselectMessages() {
    return this.occasionSelectList.deselectMessages();
  }

  toggleMessage({ checked, value }) {
    return checked
          ? this.occasion.selectMessageFrom(value)
          : this.deselectMessages();
  }

  selectLayout(layout) {
    const newState = this.layout.set(layout).svg.set(null);

    return newState.format.state
      ? newState
      : newState.format.set(layout === 'photo' ? 'center' : 'left')
  }

  get hasErrors() {
    return !!this.errors.errors.length;
  }

  get getText() {
    return this.text.content.state;
  }

  get hasText() {
    return this.text.content.state.length > 0;
  }

  get showTextPrompt() {
    return !this.hasText || this.hasText && this.isHoveringText.state;
  }

  get occasions() {
    return this.occasionSelectList.occasions;
  }

  get occasion() {
    return this.occasionSelectList.selected;
  }

  get messages() {
    return this.occasionSelectList.selected.messages;
  }

  get message() {
    return this.occasion.selected;
  }

  get maxHeight() {
    return this.layout.state === 'photo' ? 120 : 372;
  }

  get textContainerHeight() {
    /*
     * textContainer has to be visible otherwise
     * firefox throws a NS_ERROR_FAILURE error when you try to
     * getBBox() which is very unhelpful. other browsers somehow
     * handle this gracefully. this is probably an indication of
     * us doing something funky but who knows. maybe revist
     * if we have to touch this code again.
     */
    let height = 0;
    try {
      const textContainer = this.textContainer.state;
      height = textContainer ? textContainer.getBBox().height : 0;
    } catch (e) {
      /*
       * why firefox???
       */
    } finally {
      height = height || 0;
    }

    return height;
  }

  get lengthErrors() {
    let tmp = this.errors.errors.filter(x => x.validationType.state === LENGTH);
    return valueOf(tmp.errors.errors);
  }

  get hasLengthErrors() {
    let tmp = this.lengthErrors;
    return !!this.lengthErrors.length;
  }

  get specialCharErrors() {
    let tmp = this.errors.errors.filter(x => x.validationType.state === SPECIAL_CHAR);
    return valueOf(tmp.errors.errors);
  }

  get hasSpecialCharErrors() {
    return !!this.specialCharErrors.length;
  }

}
